import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const AtherosclerosisVascularDiseaseFH = () => {
  const manifestations = [
    {
      number: '1',
      title: 'Premature Atherosclerosis',
      content: '"Premature" means artery disease occurring decades earlier than expected. In untreated FH: Plaque can begin forming in teenage years, heart disease may appear in the 30s or 40s, and severe cases can affect children. Because LDL levels are elevated from birth, arteries are exposed to continuous damage over time — a concept known as lifetime LDL burden.',
      icon: 'ri-time-line',
      color: '#f04e58'
    },
    {
      number: '2',
      title: 'Endothelial Dysfunction',
      content: 'The endothelium is the thin inner lining of blood vessels. Healthy endothelium regulates blood flow, prevents clotting, and controls inflammation. High LDL in FH injures this lining early. Once damaged, the vessel becomes more permeable to cholesterol and inflammatory cells — setting the stage for plaque formation. Endothelial dysfunction is often the first detectable step toward vascular disease.',
      icon: 'ri-heart-pulse-line',
      color: '#0d9488'
    },
    {
      number: '3',
      title: 'Arterial Plaque Formation',
      content: 'When LDL particles penetrate the damaged vessel wall: They become trapped, immune cells attempt to clear them, and foam cells accumulate. This leads to fatty streaks that gradually evolve into fibrous plaques. In FH, plaque accumulation is more extensive and often affects multiple arteries simultaneously.',
      icon: 'ri-road-map-line',
      color: '#e67e22'
    },
    {
      number: '4',
      title: 'Oxidized LDL Injury',
      content: 'LDL particles can undergo chemical modification inside the artery wall, becoming oxidized LDL. Oxidized LDL triggers strong inflammatory responses, is highly toxic to vessel lining, and accelerates plaque growth. Because LDL levels are chronically elevated in FH, the amount of oxidized LDL is also increased — compounding vascular injury.',
      icon: 'ri-flask-line',
      color: '#8b5cf6'
    },
    {
      number: '5',
      title: 'Vascular Inflammation',
      content: 'Atherosclerosis is not just a cholesterol problem — it is an inflammatory disease. Persistent LDL exposure leads to: Activation of immune cells, release of inflammatory cytokines, and chronic vascular irritation. Inflammation weakens plaque structure and increases risk of rupture.',
      icon: 'ri-fire-line',
      color: '#f59e0b'
    },
    {
      number: '6',
      title: 'Plaque Instability',
      content: 'Not all plaques are equally dangerous. Stable plaques grow slowly and cause predictable symptoms. Unstable plaques have thin fibrous caps, are prone to rupture, and trigger sudden clot formation. In FH, the combination of high LDL and inflammation increases the risk of plaque instability — which can lead to sudden heart attack or stroke.',
      icon: 'ri-alert-line',
      color: '#f04e58'
    }
  ];

  const vascularDiseases = [
    {
      number: '7',
      title: 'Carotid Intima-Media Thickening',
      content: 'The carotid arteries in the neck supply blood to the brain. In FH: Ultrasound often shows thickening of the carotid artery wall. This is an early marker of atherosclerosis. Carotid intima-media thickening can be detected years before symptoms appear, offering a window for early intervention.',
      icon: 'ri-brain-line',
      color: '#0d9488'
    },
    {
      number: '8',
      title: 'Coronary Artery Calcification',
      content: 'As plaques mature, calcium deposits form inside artery walls. Coronary artery calcium (CAC) scoring: Measures calcified plaque burden and predicts heart attack risk. In FH patients, CAC scores are often elevated at younger ages, reflecting accelerated plaque progression.',
      icon: 'ri-heart-add-line',
      color: '#e67e22'
    },
    {
      number: '9',
      title: 'Peripheral Artery Disease (PAD)',
      content: 'Atherosclerosis can also affect arteries in the legs. Peripheral artery disease may cause: Leg pain during walking (claudication), slow wound healing, and reduced mobility. In severe FH, systemic plaque burden increases the likelihood of PAD.',
      icon: 'ri-walk-line',
      color: '#8b5cf6'
    },
    {
      number: '10',
      title: 'Cerebrovascular Disease',
      content: 'When plaques affect arteries supplying the brain, the risk of stroke increases. Cerebrovascular complications include: Transient ischemic attacks (mini-strokes), ischemic stroke, and cognitive impairment. Elevated LDL and Lipoprotein(a), often present in FH, further increase stroke risk.',
      icon: 'ri-mental-health-line',
      color: '#f04e58'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-16 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full text-sm text-gray-600 mb-6 shadow-sm">
              <i className="ri-heart-pulse-line text-[#f04e58]"></i>
              Understanding Vascular Disease in FH Patients
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
              Atherosclerosis & Vascular Disease + <br />
              Familial Hypercholesterolemia (FH)
            </h1>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              Understanding the hidden damage of inherited high cholesterol and how it accelerates artery disease
            </p>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Atherosclerosis & Vascular Disease: The Hidden Damage of Familial Hypercholesterolemia
            </h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                Most people think of heart disease as something that happens later in life.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                But for individuals with Familial Hypercholesterolemia (FH), artery damage can begin silently — sometimes in childhood.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                FH causes lifelong elevation of LDL ("bad") cholesterol. Over time, this excess LDL injures blood vessels, accelerates plaque formation, and dramatically increases the risk of heart attack and stroke.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                To understand why early diagnosis of FH is so critical, we must first understand how atherosclerosis and vascular disease develop.
              </p>
            </div>
          </div>

          {/* What is Atherosclerosis */}
          <div className="bg-[#fef9f9] rounded-2xl p-8 mb-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              What Is Atherosclerosis?
            </h3>
            <p className="text-gray-700 leading-relaxed">
              Atherosclerosis is the buildup of cholesterol-rich plaque inside arteries. These plaques narrow vessels, reduce blood flow, and can rupture — causing heart attacks or strokes.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              In FH, this process happens earlier, faster, and more aggressively.
            </p>
          </div>

          {/* Image */}
          <div className="w-full h-[400px] rounded-2xl overflow-hidden mb-12">
            <img
              src="https://public.readdy.ai/ai/img_res/b8d23cfc17ee112e8973ad677d41c2cb.jpg"
              alt="Atherosclerosis progression in arteries"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      {/* Manifestations Section */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-white to-[#faf5f8]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              How Atherosclerosis Develops in FH
            </h2>
            <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
              Six critical stages of vascular disease in Familial Hypercholesterolemia patients
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {manifestations.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-full text-white font-bold text-sm"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.number}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {item.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vascular Disease Beyond the Heart */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Vascular Disease Beyond the Heart
            </h2>
            <p className="text-gray-600 leading-relaxed max-w-3xl mx-auto">
              FH affects arteries throughout the body — not just coronary arteries
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {vascularDiseases.map((item, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-[#fef9f9] to-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div
                    className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: `${item.color}15` }}
                  >
                    <i className={`${item.icon} text-xl`} style={{ color: item.color }}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-full text-white font-bold text-sm"
                        style={{ backgroundColor: item.color }}
                      >
                        {item.number}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {item.title}
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {item.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why FH Changes the Timeline */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Why Familial Hypercholesterolemia Changes the Timeline
          </h2>
          <div className="bg-white rounded-2xl p-8 mb-8">
            <p className="text-gray-700 leading-relaxed mb-4">
              In the general population, atherosclerosis develops gradually with aging and lifestyle factors.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              In FH:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Elevated LDL is present from birth</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Damage accumulates every year</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#0d9488] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Arteries age faster than the person</span>
              </li>
            </ul>
            <p className="text-gray-700 leading-relaxed">
              This accelerated timeline explains why many individuals with FH experience vascular disease decades earlier than expected.
            </p>
          </div>

          {/* Silent Nature */}
          <div className="bg-gradient-to-r from-[#f04e58] to-[#e67e22] rounded-2xl p-8 text-white mb-8">
            <h3 className="text-2xl font-bold mb-4">The Silent Nature of Vascular Disease</h3>
            <p className="text-white/95 leading-relaxed mb-4">
              One of the greatest dangers of FH is that vascular injury may remain silent for years.
            </p>
            <p className="text-white/95 leading-relaxed mb-4">
              There may be:
            </p>
            <ul className="space-y-2 mb-4">
              <li className="flex items-center gap-2">
                <i className="ri-close-circle-line text-xl"></i>
                <span>No chest pain</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-close-circle-line text-xl"></i>
                <span>No warning signs</span>
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-close-circle-line text-xl"></i>
                <span>No visible symptoms</span>
              </li>
            </ul>
            <p className="text-white/95 leading-relaxed">
              Until suddenly — a heart attack or stroke occurs.
            </p>
            <p className="text-white font-semibold mt-4">
              Early screening can identify risk long before symptoms appear.
            </p>
          </div>

          {/* Power of Early Detection */}
          <div className="bg-[#0d9488] rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">The Power of Early Detection</h3>
            <p className="text-white/95 leading-relaxed mb-4">
              When FH is diagnosed early:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-check-double-line text-2xl flex-shrink-0"></i>
                <span>Aggressive LDL lowering can slow plaque formation</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-double-line text-2xl flex-shrink-0"></i>
                <span>Inflammation decreases</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-double-line text-2xl flex-shrink-0"></i>
                <span>Plaque stabilization improves</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-check-double-line text-2xl flex-shrink-0"></i>
                <span>Risk of heart attack and stroke drops dramatically</span>
              </li>
            </ul>
            <p className="text-white font-semibold">
              Modern therapies can reduce LDL by 50–70% or more — transforming long-term outcomes.
            </p>
          </div>
        </div>
      </section>

      {/* Raising Awareness */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            Raising Awareness
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gradient-to-br from-[#fef9f9] to-white rounded-2xl p-8 text-center border border-gray-100">
              <div className="text-5xl font-bold text-[#f04e58] mb-2">1 in 250</div>
              <p className="text-gray-700">People worldwide have FH</p>
            </div>
            <div className="bg-gradient-to-br from-[#fef9f9] to-white rounded-2xl p-8 text-center border border-gray-100">
              <div className="text-5xl font-bold text-[#f04e58] mb-2">90%</div>
              <p className="text-gray-700">Remain undiagnosed</p>
            </div>
          </div>

          <div className="bg-[#fef9f9] rounded-2xl p-8">
            <p className="text-gray-700 leading-relaxed mb-4">
              If you or a family member has:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">LDL cholesterol above 190 mg/dL</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Early heart disease in the family</span>
              </li>
              <li className="flex items-start gap-3">
                <i className="ri-arrow-right-s-fill text-[#f04e58] text-xl flex-shrink-0 mt-1"></i>
                <span className="text-gray-700">Stroke or vascular disease at a young age</span>
              </li>
            </ul>
            <p className="text-gray-900 font-semibold text-lg">
              Ask about FH screening.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              Because when it comes to atherosclerosis, timing is everything.
            </p>
          </div>
        </div>
      </section>

      {/* Final Takeaway */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-white mb-6 text-center">
            Final Takeaway
          </h2>
          <div className="text-center mb-8">
            <p className="text-white/90 text-lg leading-relaxed mb-4">
              Atherosclerosis and vascular disease are not sudden events — they are long, silent processes.
            </p>
            <p className="text-white/90 text-lg leading-relaxed mb-4">
              In Familial Hypercholesterolemia, that process begins early and progresses faster.
            </p>
            <p className="text-white text-xl font-semibold mb-8">
              The good news?
            </p>
            <p className="text-white/90 text-lg leading-relaxed mb-8">
              When identified early, vascular damage can be slowed, stabilized, and in many cases prevented.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-[#0d9488] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-eye-line text-3xl text-white"></i>
              </div>
              <h3 className="text-white font-semibold mb-2">Awareness</h3>
              <p className="text-white/80 text-sm">The first step</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-[#e67e22] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-test-tube-line text-3xl text-white"></i>
              </div>
              <h3 className="text-white font-semibold mb-2">Testing</h3>
              <p className="text-white/80 text-sm">The second step</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
              <div className="w-16 h-16 bg-[#f04e58] rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-heart-pulse-line text-3xl text-white"></i>
              </div>
              <h3 className="text-white font-semibold mb-2">Early Treatment</h3>
              <p className="text-white/80 text-sm">The life-saving difference</p>
            </div>
          </div>

          <p className="text-white text-lg leading-relaxed text-center">
            Awareness of FH does not just reduce cholesterol — it protects arteries for a lifetime.
          </p>
        </div>
      </section>

      {/* Key Statistics */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-r from-[#f04e58] to-[#0d9488] rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-6">Critical Statistics</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">Childhood</div>
                <p className="text-white/90 text-sm">When plaque formation can begin in FH</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">20-30</div>
                <p className="text-white/90 text-sm">Years earlier atherosclerosis develops in FH</p>
              </div>
              <div className="text-center">
                <div className="text-4xl font-bold mb-2">Multiple</div>
                <p className="text-white/90 text-sm">Arteries often affected simultaneously in FH</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-[#fef9f9] via-[#fef5f5] to-[#faf5f8]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Early Detection Saves Lives
          </h2>
          <p className="text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto">
            If you have a family history of early heart disease or high cholesterol, don't wait for symptoms. Early screening and aggressive LDL control can prevent these devastating outcomes.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/consultation"
              className="inline-flex items-center px-8 py-4 bg-[#f04e58] text-white rounded-full font-semibold hover:bg-[#d93d47] transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-[#f04e58]/20"
            >
              Book a Free Consultation
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/topics"
              className="inline-flex items-center px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-full font-semibold hover:border-[#f04e58] hover:text-[#f04e58] transition-all cursor-pointer whitespace-nowrap"
            >
              Learn More About FH
              <i className="ri-book-open-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AtherosclerosisVascularDiseaseFH;
